import { Currency } from '../../models/serializable/currency.js';
import { VersionRange } from '../../models/core/version-range.js';
import { cloneMap, DataModelBuilder, isSameData,
  isSameMap, mapToData, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { PriceRange } from '../../models/serializable/price-range.js';
import { PriceType } from '../../models/serializable/price-type.js';
import { MapUtils } from '../../utility/map-utils.js';
import { RenderableComponent } from '../core/component.js';

export class PricingDetailsComponent extends RenderableComponent {
  static readonly TYPE_ID: string = '4b59f382c31548e5828a2739e816168e';
  static readonly SCHEMA_VERSION: number = 3;
  static readonly DEFAULT_RENDER_ORDER: number = 7;

  constructor(
    public readonly priceRanges: Map<Currency, PriceRange>,
    public readonly priceType: PriceType,
    public readonly description: string,
    public readonly isPriceFixed: boolean,
    public readonly isPricePublic: boolean,
    public readonly renderOrder: number,
    public readonly isRenderingEnabled: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return PricingDetailsComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, PricingDetailsComponent.SCHEMA_VERSION);
  }

  get $removable(): boolean {
    return true;
  }

  static fromMap(context: SerializationContext, map: SerializedData): PricingDetailsComponent {
    return new PricingDetailsComponent(
        MapUtils.getMap<Currency, PriceRange>(map, 'priceRanges', (k, v) => {
          return { key: <Currency>k, value: context.fromData<PriceRange>(v as SerializedData) };
        }),
        MapUtils.get<PriceType>(map, 'priceType'),
        MapUtils.get<string>(map, 'description'),
        MapUtils.get<boolean>(map, 'isPriceFixed'),
        MapUtils.get<boolean>(map, 'isPricePublic'),
        MapUtils.get<number>(map, 'renderOrder'),
        MapUtils.get<boolean>(map, 'isRenderingEnabled'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'priceRanges': mapToData(this.priceRanges, (k, v) => {
        return { key: k.toString(), value: context.toData(v) };
      }),
      'priceType': this.priceType,
      'description': this.description,
      'isPriceFixed': this.isPriceFixed,
      'isPricePublic': this.isPricePublic,
      'renderOrder': this.renderOrder,
      'isRenderingEnabled': this.isRenderingEnabled,
    };
  }

  clone(): this {
    return new PricingDetailsComponent(
        cloneMap(this.priceRanges),
        this.priceType,
        this.description,
        this.isPriceFixed,
        this.isPricePublic,
        this.renderOrder,
        this.isRenderingEnabled,
    ) as this;
  }

  isSame(other: PricingDetailsComponent) {
    if(this === other) {
      return true;
    }

    return isSameMap(other.priceRanges, this.priceRanges, isSameData) &&
      other.priceType === this.priceType &&
      other.description === this.description &&
      other.isPriceFixed === this.isPriceFixed &&
      other.isPricePublic === this.isPricePublic &&
      other.renderOrder === this.renderOrder &&
      other.isRenderingEnabled === this.isRenderingEnabled;
  }
}

export class PricingDetailsComponentBuilder extends DataModelBuilder<PricingDetailsComponent> {
  build(context: SerializationContext, map: SerializedData): PricingDetailsComponent {
    return PricingDetailsComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(PricingDetailsComponent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(PricingDetailsComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'description', '');
      version = 2;
    }

    if(version < 3) {
      MapUtils.setIfNullOrUndefined(map, 'renderOrder', PricingDetailsComponent.DEFAULT_RENDER_ORDER);
      MapUtils.setIfNullOrUndefined(map, 'isRenderingEnabled', true);
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
